import { useRef, useState } from 'react';
import { BarChart3, Check } from 'lucide-react';
import type { Message, Poll } from '@spawnwire/shared';
import { clsx } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { formatFull } from '../../lib/time';
import { actions } from '../../store/store';
import { ui } from '../../store/ui';

/** How long is left, in the biggest unit that says it: "3 days left", "40 minutes left". */
export function timeLeft(expiresAt: string, now = Date.now()): string {
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return 'closing';
  const minutes = Math.ceil(ms / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} left`;
  const hours = Math.ceil(minutes / 60);
  if (hours < 48) return `${hours} hour${hours === 1 ? '' : 's'} left`;
  const days = Math.ceil(hours / 24);
  return `${days} days left`;
}

/** Share of voters who chose an answer, as a whole percent (people can pick several, so these may add to more than 100). */
export function percentOf(count: number, voters: number): number {
  return voters > 0 ? Math.round((count / voters) * 100) : 0;
}

/** A poll a bot posted: pick an answer, see how everyone is voting. Counts update live; who voted is not shown. */
export function PollCard({ message, poll }: { message: Pick<Message, 'id' | 'channelId'>; poll: Poll }) {
  // What the person has picked, shown at once while it is being sent. Quick clicks pile up here, and only the
  // latest picks are sent once the vote in flight is done, so none are lost.
  const [pending, setPending] = useState<number[] | null>(null);
  const inFlight = useRef(false);
  const queued = useRef<number[] | null>(null);
  /** The picks so far, updated the moment someone clicks (state only changes at the next draw). */
  const latest = useRef<number[] | null>(null);
  const mine = pending ?? poll.myVotes ?? [];
  const open = !poll.ended;
  const busy = pending !== null;

  const send = async (answerIds: number[]): Promise<void> => {
    inFlight.current = true;
    try {
      actions.setPoll(message.channelId, message.id, await http.put<Poll>(`/channels/${message.channelId}/messages/${message.id}/poll/votes`, { answerIds }));
    } catch (err) {
      queued.current = null;
      ui.toast(errorMessage(err), 'error');
    } finally {
      inFlight.current = false;
    }
    const next = queued.current;
    queued.current = null;
    if (next) await send(next);
    else {
      latest.current = null;
      setPending(null);
    }
  };

  const choose = (answerId: number) => {
    if (!open) return;
    const base = latest.current ?? poll.myVotes ?? [];
    const next = poll.allowMultiselect ? (base.includes(answerId) ? base.filter((id) => id !== answerId) : [...base, answerId]) : base.includes(answerId) ? [] : [answerId];
    latest.current = next;
    setPending(next);
    if (inFlight.current) queued.current = next;
    else void send(next);
  };

  const winner = poll.ended ? Math.max(...poll.answers.map((a) => a.count)) : -1;
  return (
    <div className="mt-1.5 w-full max-w-md rounded-lg border border-line bg-sidebar p-3" data-testid="poll">
      <div className="flex items-start gap-2">
        <BarChart3 className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
        <div className="min-w-0 font-semibold text-fg">{poll.question}</div>
      </div>
      <div className="mt-0.5 pl-6 text-xs text-muted">{poll.allowMultiselect ? 'Choose one or more' : 'Choose one'}</div>
      <ul className="mt-2 space-y-1.5" aria-label="Answers">
        {poll.answers.map((a) => {
          const chosen = mine.includes(a.id);
          const pct = percentOf(a.count, poll.totalVoters);
          const leading = poll.ended && a.count === winner && winner > 0;
          return (
            <li key={a.id}>
              <button
                type="button"
                disabled={!open}
                aria-pressed={chosen}
                aria-busy={busy}
                onClick={() => choose(a.id)}
                className={clsx(
                  'relative block w-full overflow-hidden rounded-md border px-3 py-2 text-left text-sm transition-colors',
                  chosen ? 'border-accent' : 'border-line',
                  open ? 'hover:bg-hover' : 'cursor-default',
                )}
              >
                <span className={clsx('absolute inset-y-0 left-0 transition-[width]', chosen || leading ? 'bg-accent-soft' : 'bg-active')} style={{ width: `${pct}%` }} aria-hidden />
                <span className="relative flex items-center gap-2">
                  <span className={clsx('flex size-4 shrink-0 items-center justify-center border', poll.allowMultiselect ? 'rounded' : 'rounded-full', chosen ? 'border-accent bg-accent text-accent-fg' : 'border-line-strong')} aria-hidden>
                    {chosen && <Check className="size-3" strokeWidth={3} />}
                  </span>
                  <span className={clsx('min-w-0 flex-1 break-words', leading && 'font-semibold')}>{a.text}</span>
                  <span className="shrink-0 tabular-nums text-xs text-muted">
                    {a.count} · {pct}%
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 text-xs text-muted">
        <span>
          {poll.totalVoters} {poll.totalVoters === 1 ? 'person' : 'people'} voted
        </span>
        <span title={formatFull(poll.expiresAt)}>{poll.ended ? 'Final results' : timeLeft(poll.expiresAt)}</span>
      </div>
      <div className="mt-1 text-[11px] text-faint">The bot that posted this poll can see who voted.</div>
    </div>
  );
}
